import {
  BorderOrientation,
  BorderType,
  Map as ShelfMap,
  MapComponent,
  PanoramaMetadata,
  Realogram,
  RealogramBorder,
  RealogramBorderGrouping,
  RealogramViewport,
  RealogramViewportGrouping,
  RegionGrouping,
  ScanEvent,
  SectionGrouping,
  ShelfGrouping,
  Tag,
  TagGrouping
} from '@ng-cloud/badger-core';

import * as _ from 'lodash';

export class RealogramRenderer {
  static readonly SHELVES = 1002;
  static readonly REGIONS = 1003;
  static readonly SECTIONS = 1004;
  static readonly SECTION_IDS = 1005;
  static readonly PANORAMA_BORDERS = 1006;
  static readonly VIEWPORTS = 1007;
  static readonly TAGS = 1008;
  static readonly TOP_STOCK_BORDER = 1009;

  _mapComponent: MapComponent;
  _realogram: Realogram;
  _panoramaMetadata: PanoramaMetadata;
  _shelfMap: ShelfMap;
  _scanEvent: ScanEvent;

  colorizationMode = TagGrouping.TYPE;

  groupings: number[] = [
    RealogramRenderer.SHELVES,
    RealogramRenderer.REGIONS,
    RealogramRenderer.SECTIONS,
    RealogramRenderer.SECTION_IDS,
    RealogramRenderer.PANORAMA_BORDERS,
    RealogramRenderer.VIEWPORTS,
    RealogramRenderer.TAGS,
    RealogramRenderer.TOP_STOCK_BORDER];

  shelfGrouping: ShelfGrouping;
  regionGrouping: RegionGrouping;
  sectionBorderGrouping: RealogramBorderGrouping;
  sectionGrouping: SectionGrouping;
  panoramaBorderGrouping: RealogramBorderGrouping;
  tagGrouping: TagGrouping;
  viewportGrouping: RealogramViewportGrouping;
  topStockBorderGrouping: RealogramBorderGrouping;

  constructor(groupings?: number[]) {
    if (groupings) {
      this.groupings = groupings;
    }
  }

  public get scanEvent(): ScanEvent {
    return this._scanEvent;
  }

  public set scanEvent(scanEvent: ScanEvent) {
    this._scanEvent = scanEvent;
  }

  public get mapComponent() {
    return this._mapComponent;
  }

  public set mapComponent(mapComponent: MapComponent) {
    this._mapComponent = mapComponent;

    this.createMapGroupings();
  }

  public get realogram() {
    return this._realogram;
  }

  public set realogram(realogram: Realogram) {
    this._realogram = realogram;

    if (_.isNil(realogram)) {
      this.clear();
    }
  }

  public get shelfMap() {
    return this._shelfMap;
  }

  public set shelfMap(shelfMap: ShelfMap) {
    this._shelfMap = shelfMap;

    this.groupings.includes(RealogramRenderer.PANORAMA_BORDERS) && this.panoramaBorderGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.SECTIONS) && this.sectionBorderGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.SECTION_IDS) && this.sectionGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.REGIONS) && this.regionGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.SHELVES) && this.shelfGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setStoreMap(this.shelfMap);
    this.groupings.includes(RealogramRenderer.TOP_STOCK_BORDER) && this.topStockBorderGrouping.setStoreMap(this.shelfMap);

    if (!_.isNil(this.scanEvent)) {
      this.panoramaMetadata = RealogramRenderer.panoramaMetadataFromScanEvent(this.scanEvent, this.realogram);
    }
    else {
      this.panoramaMetadata = this.realogram.panoramaMetadata;
    }

    this.processPanoramaAnnotations();
    this.processRealogram();

    this.mapComponent.render();
  }

  public get panoramaMetadata() {
    return this._panoramaMetadata;
  }

  public set panoramaMetadata(panoramaMetadata: PanoramaMetadata) {
    this._panoramaMetadata = panoramaMetadata;

    this.groupings.includes(RealogramRenderer.PANORAMA_BORDERS) && this.panoramaBorderGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.SECTIONS) && this.sectionBorderGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.SECTION_IDS) && this.sectionGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.REGIONS) && this.regionGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.SHELVES) && this.shelfGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setPanoramaMetadata(this.panoramaMetadata);
    this.groupings.includes(RealogramRenderer.TOP_STOCK_BORDER) && this.topStockBorderGrouping.setPanoramaMetadata(this.panoramaMetadata);
  }

  static panoramaMetadataFromScanEvent(scanEvent: ScanEvent, realogram: Realogram): PanoramaMetadata {
    const panoramaMetadata = new PanoramaMetadata();
    const shelfLL = scanEvent.shelfCorners[0];
    const shelfUR = scanEvent.shelfCorners[1];
    panoramaMetadata.dpi = realogram.panoramaMetadata.dpi;
    panoramaMetadata.width = scanEvent.imageWidth;
    panoramaMetadata.height = scanEvent.imageHeight;
    panoramaMetadata.shelfLL = [
      RealogramRenderer.metersToInches(shelfLL.x),
      RealogramRenderer.metersToInches(shelfLL.y),
      0];
    panoramaMetadata.shelfUR = [
      RealogramRenderer.metersToInches(shelfUR.x),
      RealogramRenderer.metersToInches(shelfUR.y),
      0];
    panoramaMetadata.shelfCorners = [
      shelfLL.x,
      shelfLL.y,
      shelfUR.x,
      shelfUR.y];
    panoramaMetadata.xShelfAlign = RealogramRenderer.metersToInches(shelfLL.x) + realogram.panoramaAnnotations.planogramLeft - shelfLL.x;
    panoramaMetadata.yShelfAlign = RealogramRenderer.metersToInches(shelfLL.y) - shelfLL.y;

    return panoramaMetadata;
  }

  static metersToInches(meters: number): number {
    return meters * 39.3701;
  }

  public createMapGroupings() {
    const options = {
      selectable: false,
      evented: false,
      scaleFactor: Math.round(this.realogram.panoramaMetadata.dpi / 10)
    };

    if (this.groupings.includes(RealogramRenderer.SHELVES)) {
      this.shelfGrouping = new ShelfGrouping(options, 'fixtures', RealogramRenderer.SHELVES);
    }

    if (this.groupings.includes(RealogramRenderer.REGIONS)) {
      this.regionGrouping = new RegionGrouping(options, 'regions', RealogramRenderer.REGIONS);
    }

    if (this.groupings.includes(RealogramRenderer.SECTIONS)) {
      this.sectionBorderGrouping = new RealogramBorderGrouping(options, 'modules', RealogramRenderer.SECTIONS);
    }

    if (this.groupings.includes(RealogramRenderer.SECTION_IDS)) {
      this.sectionGrouping = new SectionGrouping(options, 'module ids', RealogramRenderer.SECTION_IDS);
    }

    if (this.groupings.includes(RealogramRenderer.PANORAMA_BORDERS)) {
      this.panoramaBorderGrouping = new RealogramBorderGrouping(options, 'borders', RealogramRenderer.PANORAMA_BORDERS);
    }

    if (this.groupings.includes(RealogramRenderer.TAGS)) {
      this.tagGrouping = new TagGrouping(options, 'tags', RealogramRenderer.TAGS);
    }

    if (this.groupings.includes(RealogramRenderer.VIEWPORTS)) {
      this.viewportGrouping = new RealogramViewportGrouping(options, 'realogram viewports', RealogramRenderer.VIEWPORTS);
    }

    if (this.groupings.includes(RealogramRenderer.TOP_STOCK_BORDER)) {
      this.topStockBorderGrouping = new RealogramBorderGrouping(options, 'topstock border', RealogramRenderer.TOP_STOCK_BORDER);
    }

    this.groupings.includes(RealogramRenderer.PANORAMA_BORDERS) && this.mapComponent.addGrouping(this.panoramaBorderGrouping, true);
    this.groupings.includes(RealogramRenderer.SHELVES) && this.mapComponent.addGrouping(this.shelfGrouping, true);
    this.groupings.includes(RealogramRenderer.SECTIONS) && this.mapComponent.addGrouping(this.sectionBorderGrouping, true);
    this.groupings.includes(RealogramRenderer.SECTION_IDS) && this.mapComponent.addGrouping(this.sectionGrouping, true);
    this.groupings.includes(RealogramRenderer.REGIONS) && this.mapComponent.addGrouping(this.regionGrouping, true);
    this.groupings.includes(RealogramRenderer.TAGS) && this.mapComponent.addGrouping(this.tagGrouping, true);
    this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.mapComponent.addGrouping(this.viewportGrouping, true);
    this.groupings.includes(RealogramRenderer.TOP_STOCK_BORDER) && this.mapComponent.addGrouping(this.topStockBorderGrouping, true);
  }

  protected clear() {
    if (this.mapComponent) {
      this.groupings.includes(RealogramRenderer.SHELVES) && this.shelfGrouping.clearData();
      this.groupings.includes(RealogramRenderer.REGIONS) && this.regionGrouping.clearData();
      this.groupings.includes(RealogramRenderer.SECTIONS) && this.sectionBorderGrouping.clearData();
      this.groupings.includes(RealogramRenderer.SECTION_IDS) && this.sectionGrouping.clearData();
      this.groupings.includes(RealogramRenderer.PANORAMA_BORDERS) && this.panoramaBorderGrouping.clearData();
      this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.clearData();
      this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.clearData();
      this.groupings.includes(RealogramRenderer.TOP_STOCK_BORDER) && this.topStockBorderGrouping.clearData();
    }
  }

  protected processPanoramaAnnotations() {
    const panoramaAnnotations = this.realogram.panoramaAnnotations;

    if (panoramaAnnotations) {
      if (_.isNil(panoramaAnnotations.planogramLeft)) {
        panoramaAnnotations.planogramLeft = this.panoramaMetadata.xShelfAlign;
      }
      if (_.isNil(panoramaAnnotations.planogramRight)) {
        panoramaAnnotations.planogramRight = this.shelfMap.pxToM(this.shelfMap.width) + this.panoramaMetadata.xShelfAlign;
      }

      this.createPanoramaBorders();

      // Create Border Objects for sections
      const borders = _.map(_.slice(panoramaAnnotations.sections, 0, panoramaAnnotations.sections.length), section => {
        const border: RealogramBorder = new RealogramBorder();
        border.type = section.segmentBoundary === true ? BorderType.SEGMENT : BorderType.SECTION;
        border.position = section.right;
        border.length = panoramaAnnotations.globalHeight;
        return border;
      });

      const topStockBorder: RealogramBorder = new RealogramBorder();
      topStockBorder.type = BorderType.TOP_STOCK;
      topStockBorder.orientation = BorderOrientation.HORIZONTAL;
      topStockBorder.position = this.realogram.panoramaAnnotations.topStockY;
      topStockBorder.length = this.shelfMap.pxToM(this.panoramaMetadata.width, 1 / this.panoramaMetadata.dpi);

      this.groupings.includes(RealogramRenderer.SECTIONS) && this.sectionBorderGrouping.setData(borders);
      this.groupings.includes(RealogramRenderer.SECTION_IDS) && this.sectionGrouping.setData(panoramaAnnotations.sections);
      this.groupings.includes(RealogramRenderer.REGIONS) && this.regionGrouping.setData(panoramaAnnotations.regions);
      this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setPanoramaMetadata(this.panoramaMetadata);
      this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setColorMode(this.colorizationMode);
      this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.setColorMode(this.colorizationMode);
      this.groupings.includes(RealogramRenderer.TOP_STOCK_BORDER) && this.topStockBorderGrouping.setData([topStockBorder]);
    }
  }

  protected processRealogram() {
    if (this.realogram) {
      const fixtures = [];
      const viewports: RealogramViewport[] = [];
      const tags: Tag[] = [];
      // Set the absolute x position for fixtures based on their segment position
      let adjustedX = this.realogram.panoramaAnnotations.planogramLeft;

      this.realogram.planograms[0].segments.forEach(segment => {
        segment.fixtures.forEach(fixture => {
          fixture.absoluteX = adjustedX;

          fixture.realogramViewports.forEach(viewport => {
            viewport.xPosition += adjustedX;

            viewports.push(viewport);

            const tag = new Tag().fromRealogramViewport(viewport);
            tag.xPosition += adjustedX;
            tags.push(tag);
          });
        });

        fixtures.push(...segment.fixtures);
        segment.absoluteX = adjustedX;
        adjustedX = adjustedX + segment.width;
      });

      this.groupings.includes(RealogramRenderer.SHELVES) && this.shelfGrouping.setData(fixtures);
      this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.setData(viewports);
      this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setData(tags);
      this.mapComponent.render();
    }
  }

  protected createPanoramaBorders() {
    const leftBorder = new RealogramBorder();
    leftBorder.type = BorderType.PANORAMA;
    leftBorder.orientation = BorderOrientation.VERTICAL;
    leftBorder.position = this.realogram.panoramaAnnotations.planogramLeft;
    leftBorder.length = this.realogram.panoramaAnnotations.globalHeight;

    const rightBorder = new RealogramBorder();
    rightBorder.type = BorderType.PANORAMA;
    rightBorder.orientation = BorderOrientation.VERTICAL;
    rightBorder.position = this.realogram.panoramaAnnotations.planogramRight;
    rightBorder.length = this.realogram.panoramaAnnotations.globalHeight;

    const topBorder = new RealogramBorder();
    topBorder.type = BorderType.PANORAMA;
    topBorder.orientation = BorderOrientation.HORIZONTAL;
    topBorder.length = this.shelfMap.pxToM(this.panoramaMetadata.width, 1 / this.panoramaMetadata.dpi);
    topBorder.position = this.realogram.panoramaAnnotations.globalHeight;

    this.groupings.includes(RealogramRenderer.PANORAMA_BORDERS) && this.panoramaBorderGrouping.setData([leftBorder, rightBorder, topBorder]);
  }

  public setDisplayMode(displayMode: any) {
    this.colorizationMode = displayMode;

    switch (displayMode) {
      case 'type':
        this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setColorMode(TagGrouping.TYPE);
        this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.setColorMode(TagGrouping.TYPE);
        break;
      case 'unique':
        this.groupings.includes(RealogramRenderer.TAGS) && this.tagGrouping.setColorMode(TagGrouping.UNIQUE);
        this.groupings.includes(RealogramRenderer.VIEWPORTS) && this.viewportGrouping.setColorMode(TagGrouping.UNIQUE);
        break;
    }
    this.mapComponent.render();
  }
}
